import React, { useState, useRef, useCallback, useEffect } from 'react';
import { io } from 'socket.io-client';

// ─── Config ────────────────────────────────────────────────────────────────
// In dev: falls back to localhost:3005 (nodemon). In production builds the
// REACT_APP_API_URL env var is baked in at build time by react-scripts.
const API = process.env.REACT_APP_API_URL || 'http://localhost:3005';

// ─── Media type helpers ────────────────────────────────────────────────────
const IMAGE_EXT  = new Set(['jpg','jpeg','png','gif','webp','svg','bmp']);
const VIDEO_EXT  = new Set(['mp4','webm','mov','mkv','avi']);
const AUDIO_EXT  = new Set(['mp3','ogg','wav','flac','aac']);
const TEXT_EXT   = new Set(['txt','md','yml','yaml','json','sh','env','conf','log','xml','csv']);

function getExt(name) { return name.split('.').pop().toLowerCase(); }
function isImage(name)  { return IMAGE_EXT.has(getExt(name)); }
function isVideo(name)  { return VIDEO_EXT.has(getExt(name)); }
function isAudio(name)  { return AUDIO_EXT.has(getExt(name)); }
function isPreviewable(name) { return isImage(name) || isVideo(name) || isAudio(name); }

function fileIcon(file) {
  if (file.type === 'folder') return '📁';
  const ext = getExt(file.name);
  if (IMAGE_EXT.has(ext)) return '🖼';
  if (VIDEO_EXT.has(ext)) return '🎬';
  if (AUDIO_EXT.has(ext)) return '🎵';
  if (ext === 'pdf')       return '📄';
  if (ext === 'zip' || ext === 'gz' || ext === 'tar') return '📦';
  if (TEXT_EXT.has(ext))  return '📝';
  return '📎';
}

function formatSize(bytes) {
  if (!bytes || bytes === '-') return '—';
  if (bytes < 1024)        return bytes + ' B';
  if (bytes < 1024 ** 2)   return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 ** 3)   return (bytes / 1024 ** 2).toFixed(1) + ' MB';
  if (bytes < 1024 ** 4)   return (bytes / 1024 ** 3).toFixed(2) + ' GB';
  return (bytes / 1024 ** 4).toFixed(2) + ' TB';
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// ─── Socket.IO client (persistent) ─────────────────────────────────────────
const socket = io(API, { autoConnect: true });

// ════════════════════════════════════════════════════════════════════════════
export default function App() {
  // Connection state
  const [creds, setCreds]       = useState({ host: '', port: '22', username: '', password: '' });
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [homePath, setHomePath] = useState('/');

  // File browser state
  const [path, setPath]         = useState('/');
  const [files, setFiles]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState(null);   // file object
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'

  // Preview state
  const [preview, setPreview]   = useState(null); // { file, url }

  // Upload state
  const [uploads, setUploads]   = useState([]); // [{ name, pct, done, error }]

  // UI state
  const [error, setError]       = useState('');
  const [confirm, setConfirm]   = useState(null); // file to delete
  const [dragging, setDragging] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);

  const fileInputRef = useRef();
  // Always read socket.id at call time — never cache it, reconnect gives a new id
  const sid = () => socket.id;

  // ── Fetch file list ───────────────────────────────────────────────────────
  const ls = useCallback(async (p = path) => {
    if (!connected) return;
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API}/api/ls?path=${encodeURIComponent(p)}&socketId=${sid()}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setFiles(data.files);
      setPath(p);
      setSelected(null);
      if (data.isAdmin !== undefined) setIsAdmin(data.isAdmin);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [connected, path]);

  useEffect(() => { if (connected) ls(homePath); }, [connected]); // eslint-disable-line

  // Reset UI when socket disconnects or reconnects (server deletes the SFTP session)
  useEffect(() => {
    const reset = () => {
      setConnected(false); setIsAdmin(false); setHomePath('/');
      setFiles([]); setSelected(null); setPreview(null);
      setError('Connection lost — please reconnect');
    };
    socket.on('disconnect', reset);
    socket.on('connect', () => { /* new socket.id — any active SFTP session is gone */ });
    return () => { socket.off('disconnect', reset); };
  }, []);

  // ── Connect ───────────────────────────────────────────────────────────────
  async function connect() {
    setConnecting(true); setError('');
    try {
      const res  = await fetch(`${API}/api/connect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...creds, port: Number(creds.port), socketId: sid() }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setConnected(true);
      setIsAdmin(data.isAdmin || false);
      setHomePath(data.homePath || '/');
    } catch (e) {
      setError(e.message);
    } finally {
      setConnecting(false);
    }
  }

  async function disconnect() {
    await fetch(`${API}/api/disconnect`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ socketId: sid() }),
    });
    setConnected(false); setFiles([]); setPath('/'); setSelected(null); setPreview(null); setIsAdmin(false); setHomePath('/');
  }

  // ── Navigate ──────────────────────────────────────────────────────────────
  function navigate(file) {
    if (file.type !== 'folder') return;
    if (file.name === '..') {
      const parts = path.split('/').filter(Boolean);
      parts.pop();
      ls('/' + parts.join('/') || '/');
    } else {
      ls(path.replace(/\/$/, '') + '/' + file.name);
    }
  }

  function navToSegment(idx) {
    if (isAdmin) {
      const parts = path.split('/').filter(Boolean);
      ls('/' + parts.slice(0, idx).join('/'));
    } else {
      // idx 0 = '~' (home); idx 1+ = subdirs below home
      if (idx === 0) { ls(homePath); return; }
      const relParts = path.slice(homePath.length).split('/').filter(Boolean);
      ls(homePath + '/' + relParts.slice(0, idx).join('/'));
    }
  }

  // ── Download ──────────────────────────────────────────────────────────────
  function download(file) {
    const remotePath = path.replace(/\/$/, '') + '/' + file.name;
    if (file.type === 'folder') {
      window.open(`${API}/api/download/folder?path=${encodeURIComponent(remotePath)}&socketId=${sid()}`);
    } else {
      window.open(`${API}/api/download?path=${encodeURIComponent(remotePath)}&socketId=${sid()}`);
    }
  }

  // ── Preview ───────────────────────────────────────────────────────────────
  function openPreview(file) {
    const remotePath = path.replace(/\/$/, '') + '/' + file.name;
    const url = `${API}/api/preview?path=${encodeURIComponent(remotePath)}&socketId=${sid()}`;
    setPreview({ file, url });
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  async function deleteFile(file) {
    const remotePath = path.replace(/\/$/, '') + '/' + file.name;
    setError('');
    try {
      const res  = await fetch(`${API}/api/delete`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remotePath, socketId: sid() }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setConfirm(null); setSelected(null);
      ls(path);
    } catch (e) {
      setError(e.message);
    }
  }

  // ── Toggle visibility (admin only) ───────────────────────────────────────
  async function toggleHidden(file) {
    const remotePath = path.replace(/\/$/, '') + '/' + file.name;
    try {
      const res  = await fetch(`${API}/api/admin/toggle-hidden`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remotePath, socketId: sid() }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      ls(path);
    } catch (e) {
      setError(e.message);
    }
  }

  // ── New folder ────────────────────────────────────────────────────────────
  async function createFolder() {
    if (!newFolderName.trim()) return;
    const remotePath = path.replace(/\/$/, '') + '/' + newFolderName.trim();
    try {
      const res  = await fetch(`${API}/api/mkdir`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remotePath, socketId: sid() }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setNewFolderName(''); setShowNewFolder(false);
      ls(path);
    } catch (e) {
      setError(e.message);
    }
  }

  // ── Upload ────────────────────────────────────────────────────────────────
  async function uploadFiles(fileList) {
    const CHUNK_SIZE = 32 * 1024 * 1024; // 32 MB per chunk

    for (const file of Array.from(fileList)) {
      const id        = Date.now() + Math.random();
      const filename  = file.name;
      const remoteDir = path;                                          // freeze current dir
      const targetPath = remoteDir.replace(/\/$/, '') + '/' + filename;

      setUploads(u => [...u, { id, name: filename, pct: 0, done: false }]);

      // ── Check for a partial upload so we can resume ───────────────────
      let startOffset = 0;
      try {
        const prog = await fetch(
          `${API}/api/upload/progress?remotePath=${encodeURIComponent(targetPath)}&socketId=${sid()}`
        );
        if (prog.ok) {
          const { bytesWritten } = await prog.json();
          if (bytesWritten >= file.size) {
            setUploads(u => u.map(x => x.id === id ? { ...x, pct: 100, done: true } : x));
            setTimeout(() => setUploads(u => u.filter(x => x.id !== id)), 3000);
            ls(remoteDir);
            continue;
          }
          if (bytesWritten > 0) {
            startOffset = bytesWritten;
            setUploads(u => u.map(x => x.id === id
              ? { ...x, pct: Math.round(bytesWritten / file.size * 100) }
              : x));
          }
        }
      } catch (_) { /* progress check failed — start from byte 0 */ }

      // ── Send chunks sequentially ──────────────────────────────────────
      let offset      = startOffset;
      let uploadError = null;

      while (offset < file.size) {
        const chunk = file.slice(offset, offset + CHUNK_SIZE);

        const form = new FormData();
        form.append('remotePath', remoteDir);
        form.append('socketId',   sid()); // read socket.id fresh each chunk — never stale
        form.append('offset',     String(offset));
        form.append('filename',   filename);
        form.append('file',       chunk, filename);

        const result = await new Promise((resolve) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const pct = Math.round(((offset + e.loaded) / file.size) * 100);
              setUploads(u => u.map(x => x.id === id ? { ...x, pct } : x));
            }
          };
          xhr.onload = () => {
            if (xhr.status === 200) {
              resolve({ ok: true });
            } else {
              let msg = `Upload failed (HTTP ${xhr.status})`;
              try { const j = JSON.parse(xhr.responseText); msg = j.error || msg; } catch (_) {}
              resolve({ ok: false, error: msg });
            }
          };
          xhr.onerror = () => resolve({ ok: false, error: 'Network error — check your connection' });
          xhr.open('POST', `${API}/api/upload/chunk`);
          xhr.send(form);
        });

        if (result.ok) {
          offset += chunk.size;
        } else {
          uploadError = result.error;
          break;
        }
      }

      if (uploadError) {
        setUploads(u => u.map(x => x.id === id ? { ...x, error: uploadError } : x));
      } else {
        setUploads(u => u.map(x => x.id === id ? { ...x, pct: 100, done: true } : x));
        setTimeout(() => setUploads(u => u.filter(x => x.id !== id)), 3000);
        ls(remoteDir);
      }
    }
  }

  // ── Drag & drop ───────────────────────────────────────────────────────────
  function onDrop(e) {
    e.preventDefault(); setDragging(false);
    if (connected) uploadFiles(e.dataTransfer.files);
  }

  // ── Breadcrumbs ───────────────────────────────────────────────────────────
  // Non-admin: show ~ / subdir instead of the full absolute path
  const segments = isAdmin
    ? ['/', ...path.split('/').filter(Boolean)]
    : ['~', ...path.slice(homePath.length).split('/').filter(Boolean)];

  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div style={styles.app}>
      {/* ── Titlebar ── */}
      <div style={styles.titlebar}>
        <div style={styles.dots}>
          <div style={{...styles.dot, background:'#E24B4A'}} />
          <div style={{...styles.dot, background:'#EF9F27'}} />
          <div style={{...styles.dot, background:'#639922'}} />
        </div>
        <span style={styles.titleText}>
          SFTP Client {connected && (
            <span style={styles.connLabel}>
              ● {creds.username}@{creds.host}
              {isAdmin && <span style={{color:'#C97B00', marginLeft:8, fontSize:11, fontWeight:600}}>[ADMIN]</span>}
            </span>
          )}
        </span>
        <div style={{width:48}} />
      </div>

      <div style={styles.body}>
        {/* ── Sidebar ── */}
        <div style={styles.sidebar}>
          <div style={styles.sideSection}>
            <div style={styles.sideLabel}>Connection</div>
            {['host','port','username'].map(k => (
              <input key={k} style={styles.inp} placeholder={k.charAt(0).toUpperCase()+k.slice(1)}
                value={creds[k]} disabled={connected}
                onChange={e => setCreds(c => ({...c, [k]: e.target.value}))} />
            ))}
            <input style={styles.inp} type="password" placeholder="Password"
              value={creds.password} disabled={connected}
              onChange={e => setCreds(c => ({...c, password: e.target.value}))}
              onKeyDown={e => e.key === 'Enter' && !connected && connect()} />
            <button style={{...styles.btn, ...(connected ? styles.btnDanger : styles.btnPrimary)}}
              onClick={connected ? disconnect : connect} disabled={connecting}>
              {connecting ? 'Connecting…' : connected ? 'Disconnect' : 'Connect'}
            </button>
          </div>

          {connected && (
            <div style={styles.sideSection}>
              <div style={styles.sideLabel}>Quick nav</div>
              {(isAdmin
                ? ['/', `~/`, `~/Downloads`, '/var/www', '/etc', '/tmp']
                : [`~/`, `~/Downloads`, `~/Documents`]
              ).map(p => {
                const resolved = p.startsWith('~')
                  ? p.replace('~', `/home/${creds.username}`)
                  : p;
                return (
                  <div key={p} style={styles.navItem} onClick={() => ls(resolved)}>
                    <span style={{opacity:.5,fontSize:12}}>📂</span> {p}
                  </div>
                );
              })}
            </div>
          )}

          {/* Upload queue */}
          {uploads.length > 0 && (
            <div style={{...styles.sideSection, flex:1, overflow:'auto'}}>
              <div style={styles.sideLabel}>Uploading</div>
              {uploads.map(u => (
                <div key={u.id} style={styles.uploadItem}>
                  <div style={{fontSize:11, marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{u.name}</div>
                  <div style={styles.progressBg}>
                    <div style={{...styles.progressFill, width: u.pct+'%', background: u.error ? '#E24B4A' : '#185FA5'}} />
                  </div>
                  <div style={{fontSize:10, color:'#888', marginTop:2}}>{u.error || (u.done ? 'Done' : u.pct+'%')}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Main content ── */}
        <div style={styles.main}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}>

          {!connected ? (
            <div style={styles.empty}>
              <div style={{fontSize:48, marginBottom:16}}>🔒</div>
              <div style={{fontSize:16, fontWeight:500, marginBottom:8}}>Not connected</div>
              <div style={{fontSize:13, color:'#888'}}>Enter credentials and press Connect</div>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div style={styles.toolbar}>
                <div style={styles.breadcrumb}>
                  {segments.map((seg, i) => (
                    <span key={i}>
                      <span style={styles.crumb} onClick={() => navToSegment(i)}>{seg}</span>
                      {i < segments.length - 1 && <span style={{color:'#aaa', margin:'0 2px'}}>/</span>}
                    </span>
                  ))}
                </div>
                <button style={styles.tbBtn} onClick={() => ls(path)} title="Refresh">↻</button>
                <button style={styles.tbBtn} onClick={() => setViewMode(v => v === 'list' ? 'grid' : 'list')}>
                  {viewMode === 'list' ? '⊞' : '≡'}
                </button>
                <button style={styles.tbBtn} onClick={() => setShowNewFolder(true)}>+ Folder</button>
                <label style={{...styles.tbBtn, cursor:'pointer'}}>
                  ↑ Upload
                  <input ref={fileInputRef} type="file" multiple style={{display:'none'}}
                    onChange={e => uploadFiles(e.target.files)} />
                </label>
                {selected && (
                  <>
                    <button style={styles.tbBtn} onClick={() => openPreview(selected)}
                      disabled={!isPreviewable(selected.name)}>
                      👁 Preview
                    </button>
                    <button style={styles.tbBtn} onClick={() => download(selected)}>
                      {selected.type === 'folder' ? '↓ Download (.tar.gz)' : '↓ Download'}
                    </button>
                    <button style={{...styles.tbBtn, color:'#A32D2D'}} onClick={() => setConfirm(selected)}>
                      🗑 Delete
                    </button>
                    {isAdmin && (
                      <button
                        style={{...styles.tbBtn, color: selected.hidden ? '#1D9E75' : '#888', borderColor: selected.hidden ? '#A8D5C4' : '#d0d0d0'}}
                        onClick={() => toggleHidden(selected)}
                        title={selected.hidden ? 'Show to regular users' : 'Hide from regular users'}>
                        {selected.hidden ? '👁 Show users' : '🚫 Hide users'}
                      </button>
                    )}
                  </>
                )}
              </div>

              {error && (
                <div style={styles.errorBar}>{error} <span style={{cursor:'pointer', marginLeft:8}} onClick={() => setError('')}>✕</span></div>
              )}

              {showNewFolder && (
                <div style={styles.newFolderBar}>
                  <input autoFocus style={{...styles.inp, marginBottom:0, flex:1}}
                    placeholder="Folder name"
                    value={newFolderName}
                    onChange={e => setNewFolderName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') setShowNewFolder(false); }} />
                  <button style={styles.tbBtn} onClick={createFolder}>Create</button>
                  <button style={styles.tbBtn} onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}>Cancel</button>
                </div>
              )}

              {/* Drop overlay */}
              {dragging && (
                <div style={styles.dropOverlay}>
                  <div style={{fontSize:40}}>☁</div>
                  <div style={{fontWeight:500}}>Drop to upload</div>
                </div>
              )}

              {/* File listing */}
              {loading ? (
                <div style={styles.empty}><div style={{fontSize:24}}>⏳</div><div>Loading…</div></div>
              ) : viewMode === 'list' ? (
                <div style={styles.fileArea}>
                  <div style={styles.colHead}>
                    <span style={{gridColumn:'1/3'}}>Name</span>
                    <span>Size</span><span>Modified</span><span>Permissions</span>
                  </div>
                  {/* Parent dir — hidden at home root for regular users */}
                  {path !== '/' && (isAdmin || path !== homePath) && (
                    <div style={styles.fileRow} onDoubleClick={() => navigate({type:'folder',name:'..'})}>
                      <span>📁</span><span style={{color:'#888'}}>..</span><span /><span /><span />
                    </div>
                  )}
                  {files.map(f => (
                    <div key={f.name}
                      style={{...styles.fileRow,
                        background: selected?.name === f.name ? 'rgba(24,95,165,0.10)' : '',
                        opacity: isAdmin && f.hidden ? 0.45 : 1,
                      }}
                      onClick={() => setSelected(s => s?.name === f.name ? null : f)}
                      onDoubleClick={() => f.type === 'folder' ? navigate(f) : openPreview(f)}>
                      <span style={{fontSize:16}}>{fileIcon(f)}</span>
                      <span style={{
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                        fontWeight: f.type==='folder'?500:400,
                        textDecoration: isAdmin && f.hidden ? 'line-through' : 'none',
                        color: isAdmin && f.hidden ? '#aaa' : 'inherit',
                      }}>
                        {f.name}
                        {isAdmin && f.hidden && (
                          <span style={{fontSize:9, marginLeft:5, color:'#bbb', fontStyle:'italic', textDecoration:'none'}}>hidden</span>
                        )}
                      </span>
                      <span style={styles.meta}>{f.type === 'folder' ? '—' : formatSize(f.size)}</span>
                      <span style={styles.meta}>{formatDate(f.modified)}</span>
                      <span style={{...styles.meta, fontFamily:'monospace', fontSize:11}}>{f.perms}</span>
                    </div>
                  ))}
                </div>
              ) : (
                /* Grid / thumbnail view */
                <div style={styles.fileArea}>
                  <div style={styles.gridView}>
                    {path !== '/' && (isAdmin || path !== homePath) && (
                      <div style={styles.gridItem} onDoubleClick={() => navigate({type:'folder',name:'..'})}>
                        <div style={styles.gridThumb}>📁</div>
                        <div style={styles.gridName}>..</div>
                      </div>
                    )}
                    {files.map(f => (
                      <div key={f.name}
                        style={{...styles.gridItem,
                          outline: selected?.name === f.name ? '2px solid #185FA5' : 'none',
                          opacity: isAdmin && f.hidden ? 0.45 : 1,
                        }}
                        onClick={() => setSelected(s => s?.name === f.name ? null : f)}
                        onDoubleClick={() => f.type === 'folder' ? navigate(f) : openPreview(f)}>
                        <div style={styles.gridThumb}>
                          {isImage(f.name)
                            ? <img src={`${API}/api/preview?path=${encodeURIComponent(path.replace(/\/$/,'') + '/' + f.name)}&socketId=${sid()}`}
                                alt={f.name} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:6}} />
                            : <span style={{fontSize:32}}>{fileIcon(f)}</span>}
                        </div>
                        <div style={{...styles.gridName, textDecoration: isAdmin && f.hidden ? 'line-through' : 'none', color: isAdmin && f.hidden ? '#aaa' : 'inherit'}} title={f.name}>{f.name}</div>
                        <div style={{...styles.meta, fontSize:10, textAlign:'center'}}>{f.type==='folder'?'folder':formatSize(f.size)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Preview panel ── */}
        {preview && (
          <div style={styles.previewPanel}>
            <div style={styles.previewHeader}>
              <span style={{fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1}}>
                {preview.file.name}
              </span>
              <button style={styles.closeBtn} onClick={() => setPreview(null)}>✕</button>
            </div>
            <div style={styles.previewBody}>
              {isImage(preview.file.name) && (
                <img src={preview.url} alt={preview.file.name}
                  style={{maxWidth:'100%', maxHeight:'100%', objectFit:'contain', borderRadius:6}} />
              )}
              {isVideo(preview.file.name) && (
                <video controls style={{width:'100%', borderRadius:6}} src={preview.url}>
                  Your browser doesn't support video.
                </video>
              )}
              {isAudio(preview.file.name) && (
                <div style={{padding:16, width:'100%'}}>
                  <div style={{fontSize:32, textAlign:'center', marginBottom:12}}>🎵</div>
                  <audio controls style={{width:'100%'}} src={preview.url} />
                </div>
              )}
              {!isPreviewable(preview.file.name) && (
                <div style={{textAlign:'center', padding:32, color:'#888'}}>
                  <div style={{fontSize:48, marginBottom:8}}>{fileIcon(preview.file)}</div>
                  <div>No preview available</div>
                </div>
              )}
            </div>
            <div style={styles.previewFooter}>
              <span style={styles.meta}>{formatSize(preview.file.size)}</span>
              <button style={styles.tbBtn} onClick={() => download(preview.file)}>↓ Download</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Delete confirm modal ── */}
      {confirm && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={{fontWeight:500, marginBottom:8}}>Delete "{confirm.name}"?</div>
            <div style={{fontSize:13, color:'#666', marginBottom:20}}>
              This permanently removes the {confirm.type} from the server.
            </div>
            <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
              <button style={styles.tbBtn} onClick={() => setConfirm(null)}>Cancel</button>
              <button style={{...styles.tbBtn, background:'#FCEBEB', color:'#A32D2D', borderColor:'#F09595'}}
                onClick={() => deleteFile(confirm)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  app:        { display:'flex', flexDirection:'column', height:'100vh', fontFamily:'system-ui,sans-serif', fontSize:13, color:'#1a1a1a', background:'#fff' },
  titlebar:   { display:'flex', alignItems:'center', gap:10, padding:'8px 16px', borderBottom:'0.5px solid #e0e0e0', background:'#f8f8f8', minHeight:40 },
  dots:       { display:'flex', gap:6 },
  dot:        { width:12, height:12, borderRadius:'50%' },
  titleText:  { flex:1, textAlign:'center', fontSize:13, color:'#555' },
  connLabel:  { color:'#1D9E75', marginLeft:8, fontFamily:'monospace' },
  body:       { display:'flex', flex:1, overflow:'hidden' },

  sidebar:    { width:200, borderRight:'0.5px solid #e0e0e0', display:'flex', flexDirection:'column', background:'#fafafa', overflowY:'auto' },
  sideSection:{ padding:'12px 12px 10px', borderBottom:'0.5px solid #e8e8e8' },
  sideLabel:  { fontSize:10, fontWeight:600, color:'#aaa', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 },
  inp:        { display:'block', width:'100%', padding:'5px 8px', fontSize:12, border:'0.5px solid #d0d0d0', borderRadius:6, background:'#fff', color:'#1a1a1a', marginBottom:6, fontFamily:'monospace', outline:'none' },
  btn:        { width:'100%', padding:'6px', fontSize:12, fontWeight:500, border:'0.5px solid #ccc', borderRadius:6, cursor:'pointer', marginTop:2 },
  btnPrimary: { background:'#fff', color:'#185FA5', borderColor:'#B5D4F4' },
  btnDanger:  { background:'#FCEBEB', color:'#A32D2D', borderColor:'#F09595' },
  navItem:    { display:'flex', alignItems:'center', gap:6, padding:'5px 4px', fontSize:12, color:'#555', cursor:'pointer', borderRadius:4, fontFamily:'monospace' },
  uploadItem: { marginBottom:10 },
  progressBg: { height:3, background:'#e8e8e8', borderRadius:2, overflow:'hidden' },
  progressFill:{ height:'100%', borderRadius:2, transition:'width .2s' },

  main:       { flex:1, display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' },
  toolbar:    { display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderBottom:'0.5px solid #e0e0e0', flexWrap:'wrap' },
  breadcrumb: { flex:1, display:'flex', alignItems:'center', gap:2, fontFamily:'monospace', fontSize:12, overflow:'hidden' },
  crumb:      { color:'#185FA5', cursor:'pointer', whiteSpace:'nowrap' },
  tbBtn:      { display:'inline-flex', alignItems:'center', gap:4, padding:'4px 10px', fontSize:12, border:'0.5px solid #d0d0d0', borderRadius:6, background:'#fff', color:'#1a1a1a', cursor:'pointer', whiteSpace:'nowrap' },
  errorBar:   { display:'flex', alignItems:'center', padding:'6px 14px', background:'#FCEBEB', color:'#A32D2D', fontSize:12, borderBottom:'0.5px solid #F09595' },
  newFolderBar:{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderBottom:'0.5px solid #e0e0e0', background:'#f8f8f8' },
  fileArea:   { flex:1, overflowY:'auto', padding:'0 0 8px' },
  colHead:    { display:'grid', gridTemplateColumns:'24px 1fr 90px 150px 110px', gap:8, padding:'6px 12px', fontSize:11, fontWeight:500, color:'#aaa', borderBottom:'0.5px solid #e0e0e0', position:'sticky', top:0, background:'#fff', zIndex:1 },
  fileRow:    { display:'grid', gridTemplateColumns:'24px 1fr 90px 150px 110px', gap:8, padding:'5px 12px', alignItems:'center', cursor:'pointer', borderRadius:6, margin:'1px 6px' },
  meta:       { color:'#888', fontSize:11, fontFamily:'monospace' },
  gridView:   { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(110px, 1fr))', gap:10, padding:12 },
  gridItem:   { display:'flex', flexDirection:'column', alignItems:'center', padding:8, borderRadius:8, border:'0.5px solid #e8e8e8', cursor:'pointer', background:'#fff', gap:6 },
  gridThumb:  { width:80, height:80, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:6, overflow:'hidden', background:'#f4f4f4' },
  gridName:   { fontSize:11, textAlign:'center', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', width:'100%' },
  empty:      { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#aaa', gap:8 },
  dropOverlay:{ position:'absolute', inset:0, background:'rgba(24,95,165,0.12)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, fontSize:18, color:'#185FA5', zIndex:5, border:'2px dashed #185FA5', borderRadius:8, margin:8 },

  previewPanel:{ width:300, borderLeft:'0.5px solid #e0e0e0', display:'flex', flexDirection:'column', background:'#fafafa' },
  previewHeader:{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', borderBottom:'0.5px solid #e0e0e0' },
  closeBtn:   { background:'none', border:'none', cursor:'pointer', fontSize:16, color:'#888', padding:'0 4px', lineHeight:1 },
  previewBody:{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:12, overflow:'auto' },
  previewFooter:{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', borderTop:'0.5px solid #e0e0e0' },

  modalBackdrop:{ position:'fixed', inset:0, background:'rgba(0,0,0,.35)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100 },
  modal:      { background:'#fff', border:'0.5px solid #d0d0d0', borderRadius:10, padding:20, minWidth:280, maxWidth:360, boxShadow:'0 8px 32px rgba(0,0,0,.12)' },
};
